import axios from 'axios';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import accountService from 'services/account';
import apiService from 'services/api';
import batchService from 'services/batch';
import { Box, Button, Icon, ResponsiveDialog, Stack, styled, Text } from 'shell/components';
import { SECTION_URL } from 'utils/constants';
import { v4 as uuid } from 'uuid';

const LoadingIndicator = () => {
    return (
        <LoadingStack direction={'row'} role={'progressbar'}>
            <LoadingLeftBox>.</LoadingLeftBox>
            <LoadingRightStack direction={'row'}>..</LoadingRightStack>
        </LoadingStack>
    );
};

const SyncRosterDialog = ({ open, onClose }) => {
    const { accountUsers, setAccountUsers, removeAccountUser } = useApp();
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const { device } = useViewport();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('get-roster');
    const [data, setData] = useState({});

    const handleClose = useCallback(() => {
        setLoading(false);
        setData({});
        onClose();
    }, [onClose]);

    const getAPICourseUsers = useCallback(
        async (accessToken) => {
            try {
                let usersMap = {};
                let url = null;
                let sections = [];
                const course_id = auth.course_id;

                const section_pre = await apiService.getEnvData({
                    course_id,
                    accessToken,
                    assignment_id: '',
                    url,
                });

                const section_req_body = {
                    course_id,
                    accessToken,
                    assignment_id: '',
                    API_URL: section_pre.API_URL,
                    PLATFORM: section_pre.PLATFORM,
                };

                const roster = await fetch(SECTION_URL, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(section_req_body),
                })
                    .then(async (res) => await res.json())
                    .catch((e) => new Error('Section fetch error: ' + e.message));
                if (roster?.students?.length) {
                    // D2L Case
                    if (process.env.REACT_APP_KIND === 'D2L') {
                        roster?.students?.forEach((student) => {
                            const user_id = `${student.id}`;

                            if (!usersMap[user_id]) {
                                // First time we see this student
                                usersMap[user_id] = {
                                    ...student,
                                    user_id,
                                    role: 'student',
                                    username: `${student.login_id}`,
                                    email: student.email,
                                };
                            }
                        });
                    } else if (process.env.REACT_APP_KIND === 'CANVAS') {
                        // Canvas Case
                        sections = roster?.students;
                        sections.forEach((section) => {
                            const normalizedSection = {
                                id: `${section.id}`,
                                name: section.name,
                                course_id: `${section.course_id}`,
                                start_at: section.start_at ? new Date(section.start_at).getTime() : null,
                                end_at: section.end_at ? new Date(section.end_at).getTime() : null,
                                created_at: section.created_at ? new Date(section.created_at).getTime() : null,
                                restrict_enrollments_to_section_dates: section.restrict_enrollments_to_section_dates,
                                nonxlist_course_id: section.nonxlist_course_id,
                                sis_section_id: section.sis_section_id,
                                sis_course_id: section.sis_course_id,
                                integration_id: section.integration_id,
                                sis_import_id: section.sis_import_id,
                            };

                            section?.students?.forEach((student) => {
                                const user_id = `${student.id}`;

                                if (!usersMap[user_id]) {
                                    // First time we see this student
                                    usersMap[user_id] = {
                                        ...student,
                                        user_id,
                                        role: 'student',
                                        username: `${student.login_id}`,
                                        email: student.email,
                                        sections: [normalizedSection],
                                    };
                                } else {
                                    // Student already exists → append section
                                    usersMap[user_id].sections.push(normalizedSection);
                                }
                            });
                        });
                    }
                }
                if (roster?.teachers?.length) {
                    // D2L Case
                    if (process.env.REACT_APP_KIND === 'D2L') {
                        roster?.teachers?.forEach((teacher) => {
                            const user_id = `${teacher.id}`;

                            if (!usersMap[user_id]) {
                                // First time we see this teacher
                                usersMap[user_id] = {
                                    ...teacher,
                                    user_id,
                                    role: teacher.role,
                                    username: `${teacher.login_id}`,
                                    email: teacher.email,
                                };
                            }
                        });
                    } else if (process.env.REACT_APP_KIND === 'CANVAS') {
                        // Canvas Case
                        roster?.teachers?.forEach((teacher) => {
                            const user_id = `${teacher.id}`;

                            if (!usersMap[user_id]) {
                                // First time we see this teacher
                                usersMap[user_id] = {
                                    ...teacher,
                                    user_id,
                                    username: `${teacher.login_id}`,
                                    email: teacher.email,
                                    role: teacher.role,
                                    sections: [],
                                };
                            }
                        });
                    }
                }
                // Return as an array
                return Object.values(usersMap);
            } catch (e) {
                console.log('error fetching roster:', e);
                setLoading(false);
                return message.error(
                    t(
                        'message.fetch-roster-fail',
                        'There was an error fetching the roster. Please launch TimelyGrader again.'
                    )
                );
            }
        },
        [auth?.course_id, message, t]
    );

    function findNewSections(array1, array2) {
        const ids1 = new Set((array1 || []).map((s) => s.id));
        return array2.filter((s) => !ids1.has(s.id));
    }

    function hasMatchingSection(array1, array2) {
        const ids = new Set((array1 || []).map((s) => s.id));
        return array2.some((s) => ids.has(s.id));
    }
    const getLatestRoster = useCallback(async () => {
        const accessToken = localStorage.getItem('access_token');
        const normalize = (v) => (v === undefined || v === null ? null : String(v).trim().toLowerCase());

        // Decide when a Canvas user and a DB user are actually the same student
        const isSameStudent = (a, b) => {
            // 1) login/email match
            let compA = {
                email: normalize(a.email),
                username: normalize(a.username),
                login_id: normalize(a.login_id),
                sis_login_id: normalize(a.sis_login_id),
                user_id: normalize(a.user_id),
            };
            let compB = {
                email: normalize(b.email),
                username: normalize(b.username),
                login_id: normalize(b.login_id),
                sis_login_id: normalize(b.sis_login_id),
                user_id: normalize(b.user_id),
            };

            if (compA.user_id && compB.user_id && `${compA.user_id}` === `${compB.user_id}`) {
                return true;
            }
            if (compA.email && compB.email && `${compA.email}` === `${compB.email}`) {
                return true;
            }
            if (compA.username && compB.username && `${compA.username}` === `${compB.username}`) {
                return true;
            }
            if (compA.login_id && compB.login_id && `${compA.login_id}` === `${compB.login_id}`) {
                return true;
            }
            if (compA.sis_login_id && compB.sis_login_id && `${compA.sis_login_id}` === `${compB.sis_login_id}`) {
                return true;
            }
            return false;
        };

        try {
            const courseUsers = await getAPICourseUsers(accessToken);
            if (!courseUsers || courseUsers.length === 0) {
                throw new Error('No users found in course roster');
            }
            const filterUsersWithSameSections = courseUsers.filter((user) =>
                user.role !== 'instructor' ? hasMatchingSection(user.sections || [], auth?.sections || []) : true
            );
            const mergeAccountAndUsers = accountUsers
                .filter((user) =>
                    user.role !== 'instructor' ? hasMatchingSection(user.sections, auth?.sections || []) : true
                )
                .map((user) => ({
                    ...user,
                    id: user.owner_account_id,
                    user_id: `${user.user_id}`,
                    username: user.username,
                    email: user.email || user.username,
                }))
                .filter(Boolean);
            // Students in DB but not in Canvas
            const findDeletion = mergeAccountAndUsers.filter(
                (localUser) =>
                    localUser.role === 'student' &&
                    !filterUsersWithSameSections.some((canvasUser) => isSameStudent(localUser, canvasUser))
            );
            // Students in Canvas but not in DB
            const findAddition = filterUsersWithSameSections.filter(
                (canvasUser) => !mergeAccountAndUsers.some((localUser) => isSameStudent(localUser, canvasUser))
            );

            let toDeleteArray = [];
            let toAddArray = [];
            let toUpdateArray = [];

            // Deletions: all local students no longer in Canvas
            findDeletion.forEach((member) => {
                // you already know there's no matching Canvas user because of the filter above
                // just make sure they are still valid student accounts
                if (member.active) {
                    toDeleteArray = [...toDeleteArray, member];
                }
            });
            // Additions + updates
            findAddition.forEach((member) => {
                // Check if we actually have a matching DB user already
                const existing = mergeAccountAndUsers.find((user) => isSameStudent(user, member));
                // Brand new student: needs to be added
                if (!existing) {
                    const newMember = {
                        ...member,
                        active: true,
                        id: uuid(),
                        role: member.role,
                        user_id: `${member.user_id}`,
                        owner_account_id: member.owner_account_id,
                    };
                    toAddArray = [...toAddArray, newMember];
                }
            });

            // Handle "update" case: student exists in DB, but sections need updating
            filterUsersWithSameSections.forEach((member) => {
                const existing = mergeAccountAndUsers.find((user) => isSameStudent(user, member));

                if (existing && findNewSections(existing.sections || [], member.sections || []).length > 0) {
                    const userData = {
                        ...existing,
                        ...member,
                    };
                    toUpdateArray = [...toUpdateArray, userData];
                }
            });
            return {
                toDeleteArray,
                toAddArray,
                toUpdateArray,
            };
        } catch (e) {
            console.log('error fetching roster:', e);
            message.error(
                t(
                    'message.fetch-roster-fail',
                    'There was an error fetching the roster. Please launch TimelyGrader again.'
                )
            );
            return handleClose();
        }
    }, [accountUsers, auth?.sections, getAPICourseUsers, handleClose, message, t]);

    const getRoster = useCallback(async () => {
        // const minDelay = new Promise((resolve) => setTimeout(resolve, 3000));
        const refreshTokenUrl = localStorage.getItem('refresh_token');
        try {
            if (refreshTokenUrl) {
                // Attempting token refresh
                const newToken = await axios.post('/get-refresh-token', {
                    code: refreshTokenUrl,
                    course_id: localStorage.getItem('course_id'),
                });
                if (newToken.access_token) {
                    localStorage.setItem('access_token', newToken.access_token);
                }
                if (newToken.refresh_token) {
                    localStorage.setItem('refresh_token', newToken.refresh_token);
                }
            } else {
                throw new Error('No refresh token found');
            }
        } catch (e) {
            console.log('error fetching roster:', e);
            return message.error(
                t(
                    'message.fetch-roster-fail',
                    'There was an error fetching the roster. Please launch TimelyGrader again.'
                )
            );
        }

        const rosterPromise = getLatestRoster();

        const [rosterData] = await Promise.all([rosterPromise]);
        if (
            !rosterData?.toAddArray?.length &&
            !rosterData?.toDeleteArray?.length &&
            !rosterData?.toUpdateArray?.length
        ) {
            message.success(t('message.roster-up-to-date', 'Your roster is up to date.'));
            onClose();
            return;
        }
        setData(rosterData);
        setStep('sync-roster');
    }, [getLatestRoster, message, onClose, t]);

    const removeUser = async (accountUser) => {
        await accountService.deleteAccount(accountUser.id);
        removeAccountUser(accountUser);
    };

    const updateUser = async (accountUser) => {
        if (!accountUser.sections?.length) return;
        if (!accountUser.owner_account_id) return;
        await accountService.updateAccount(accountUser.owner_account_id, {
            sections: [...accountUser.sections],
        });
    };

    const chunk = (arr, size) => {
        const out = [];
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
        return out;
    };

    const BATCH_SIZE = 50;

    const syncRoster = async () => {
        const { toDeleteArray, toAddArray, toUpdateArray } = data;
        if (!toDeleteArray?.length && !toAddArray?.length && !toUpdateArray?.length) return;

        setLoading(true);

        // deletes can stay as-is (or chunk later if needed)
        await Promise.all(toDeleteArray.map(removeUser));

        // Build the list of users that need to be created/invited.
        // Include role on each user (student / instructor / ta).
        const usersToInvite = [...toAddArray].map((u) => ({
            ...u,
            active: true,
            // keep role from roster (important for mixed roles)
            role: u.role,
            // normalize for backend
            username: u.username || u.email,
            email: u.email || u.username,
            sections: [...(u.sections ?? [])],
            user_id: `${u.user_id}`,
        }));

        // Send in chunks of 50 (sequential, to avoid stampeding your API)
        for (const usersChunk of chunk(usersToInvite, BATCH_SIZE)) {
            await batchService.sendInvitations({
                data: {
                    is_lti: true,
                    root_url: window.location.origin,
                    course_id: auth.course_id,
                    // no "type" here: backend will split by role
                    users: usersChunk,
                },
            });
        }

        // Updates can stay the same (or chunk later)
        await Promise.all(toUpdateArray.map(updateUser));

        setData({});
        setLoading(false);
        message.success(t('message.roster-sync-success', 'Your roster has been synced.'));
        handleClose();

        // Your existing merge logic can remain (though it currently doesn’t use the API response)
        const uniqueByUserId = (users) => Array.from(new Map(users.map((u) => [u.user_id, u])).values());
        const uniqueByUserName = (users) => Array.from(new Map(users.map((u) => [u.username, u])).values());

        let addedUsers = [...toAddArray, ...toUpdateArray].map((user) => ({
            ...user,
            active: true,
            role: user.role,
            id: user.id,
            owner_account_id: user.id,
            username: user.username || user.email,
            email: user.email || user.username,
        }));

        let result = uniqueByUserId(
            [...accountUsers, ...addedUsers].filter(
                (user) => !toDeleteArray.some((del) => del.user_id === user.user_id)
            )
        );
        result = uniqueByUserName(result);
        setAccountUsers(result);
    };

    useEffect(() => {
        if (open) {
            // reset immediately when opening
            setLoading(true);

            (async () => {
                await getRoster();
                // getRoster already sets step/data, but if you want:
                // setData(rosterData || {});
                setLoading(false);
            })();
        } else {
            // optional: no need for timeout here unless you like drama
            setLoading(false);
            setStep('get-roster');
            setData({});
        }
    }, [getRoster, open]);

    const renderGettingRoster = () => {
        return (
            <RootStack alignItems={'center'} device={device} spacing={5}>
                <Stack alignItems={'center'} spacing={3}>
                    <Icon sx={{ fontSize: '2rem' }} kind={'clock'} />
                    <Stack alignItems={'center'} direction={'row'}>
                        <Text id={'dialog-title'} align={'center'} component={'h1'} variant={'h6'}>
                            {t('people.sync-roster.process-title', 'Please wait while we check your member roster')}
                        </Text>
                        <LoadingIndicator />
                    </Stack>
                </Stack>
                <Text sx={{ maxWidth: 350 }} align={'center'}>
                    {t(
                        'people.sync-roster.process-description',
                        'This process may vary in speed depending on class size, so please be patient while we complete the verification.'
                    )}
                </Text>
                <Button color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                    {t('controls.cancel', 'Cancel')}
                </Button>
            </RootStack>
        );
    };

    const renderConfirmSyncRoster = () => {
        return (
            <RootStack alignItems={'center'} device={device} spacing={5}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <CustomIcon kind={'error'} />
                    <Text id={'dialog-title'} align={'center'} component={'h1'} variant={'h6'}>
                        {t('people.sync-roster.results-title', 'The following changes were found')}
                    </Text>
                </Stack>
                <Text sx={{ maxWidth: 350 }} align={'center'}>
                    {t(
                        'people.sync-roster.results-description',
                        'Please sync your member roster below to ensure that you are using the most current version.'
                    )}
                </Text>
                <Stack sx={{ mt: 0 }} component={'ul'} spacing={1}>
                    <Text component={'li'}>
                        {t('people.sync-roster.users-added', '{{count}} user(s) will be added', {
                            count: data?.toAddArray?.length || 0,
                        })}
                    </Text>
                    <Text component={'li'}>
                        {t('people.sync-roster.users-removed', '{{count}} user(s) will be removed', {
                            count: data?.toDeleteArray?.length || 0,
                        })}
                    </Text>
                    <Text component={'li'}>
                        {t('people.sync-roster.users-updated', '{{count}} user(s) will be updated', {
                            count: data?.toUpdateArray?.length || 0,
                        })}
                    </Text>
                </Stack>
                <Stack sx={{ mt: 2 }} alignItems={'center'} direction={'row'} spacing={3}>
                    <Button color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                        {t('controls.cancel', 'Cancel')}
                    </Button>
                    <Button loading={loading} onClick={() => syncRoster()}>
                        {t('controls.sync-roster', 'Sync Roster')}
                    </Button>
                </Stack>
            </RootStack>
        );
    };

    return (
        <ResponsiveDialog aria-labelledby={'dialog-title'} open={open} onClose={handleClose}>
            {step === 'get-roster' ? renderGettingRoster() : renderConfirmSyncRoster()}
        </ResponsiveDialog>
    );
};

const LoadingStack = styled(Stack)(() => {
    return {
        opacity: 1,
    };
});

const LoadingLeftBox = styled(Box)(() => {
    return {
        fontSize: 20,
        animation: 'loading-left 1s ease-out infinite',
        '@keyframes loading-left': {
            '7.5%': {
                transform: 'translate(50%, -15%)',
            },
            '15%': {
                transform: 'translate(100%, -20%)',
            },
            '22.5%': {
                transform: 'translate(175%, -15%)',
            },
            '30%, 100%': {
                transform: 'translate(200%, 0)',
            },
        },
    };
});

const LoadingRightStack = styled(Stack)(() => {
    return {
        fontSize: 20,
        animation: 'loading-right 1s ease-in-out infinite',
        '@keyframes loading-right': {
            '30%, 100%': {
                transform: 'translateX(-50%)',
            },
        },
    };
});

export const RootStack = styled(Stack)(({ device }) => {
    return {
        width: device !== 'mobile' ? '620px' : '100%',
        ...(device === 'mobile' && {
            marginTop: '25vh',
        }),
    };
});

export const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        padding: '0.3125rem',
        fontSize: '1.5rem',
        background: theme.palette.ensightful.primary[500],
        color: theme.palette.ensightful.shades[0],
    };
});

export default SyncRosterDialog;
